import ProductAddToCartHOC from '@common/components/ProductAddToCartHOC';
import Product from '@common/models/Product';
import { addProductToCart } from '@common/store/cart/slice';
import { addToast } from '@common/store/toasts/slice';
import { getBuyerJWTToken } from '@common/utils/token';
import Gallery from '@components/Gallery';
import ProductAddToCart from '@components/ProductAddToCart';
import StarRating from '@components/Rating/StarRating';
import ShareModal from '@components/ShareModal';
import WishlistIcon from '@components/WishlistIcon';
import Editor from '@lib/Editor';
import classnames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';

export interface ProductProps {
  type?: string;
  isZoomEnabled?: boolean;
  quickView?: boolean;
  product: Product;
  cartProductQuantity: number;
  review: number;
  addToast: typeof addToast;
  addProductToCart: typeof addProductToCart;
  storeType: string;
  wishListFeature: boolean;
  productVideoFeature: boolean;
}

const ProductComponent: React.FunctionComponent<ProductProps> = ({
  quickView,
  product,
  cartProductQuantity,
  addToast,
  review,
  reviewsCount,
  addProductToCart,
  storeType,
  wishListFeature,
  productVideoFeature,
}) => {
  return (
    <ProductAddToCartHOC
      variantSetToMinQuantity={true}
      disableSuccessToasts={() => false}
      product={product}
      render={(hocArgs) => {
        const { finalProduct, onWishListClickHandler, wishlist } = hocArgs;
        const StarRatingValue = finalProduct.averageRating;
        return (
          <div
            className={classnames('mx-auto', {
              'md:container mb-48': !quickView,
            })}
          >
            <div
              className={classnames('flex flex-col md:flex-row', {
                'justify-center': quickView,
              })}
            >
              <div
                className={classnames('relative', {
                  'w-full md:flex-670 md:max-w-670 mb-16': !quickView,
                  'w-full md:w-394 mr-16': quickView,
                })}
              >
                <Gallery
                  images={finalProduct.images.length ? finalProduct.images : product.images}
                  quickView={quickView}
                  imageDescription={`${finalProduct.images.length ? finalProduct.title : product.title}${
                    finalProduct?.optionValues?.length ? '_' + finalProduct?.optionValues.join('_') : ''
                  }`}
                  productVideoUrl={finalProduct.productVideoUrl}
                  productVideoEnabled={productVideoFeature ? finalProduct.productVideoEnabled : false}
                  product={product}
                  finalProduct={finalProduct}
                />
              </div>
              <div
                className={classnames('', {
                  'flex-1 px-16 md:pl-16 md:pr-0 md:min-w-400 mb-32 md:mb-0 z-1 relative': !quickView,
                  'flex-1 px-16 md:pr-0 mb-32 md:mb-0 md:w-394': quickView,
                })}
              >
                <div
                  className={classnames('text-center md:text-left', {
                    'md:sticky md:top-150': !quickView,
                  })}
                >
                  <div className="flex mb-8 items-center">
                    <div className="flex-1 flex justify-center md:justify-start">
                      {parseInt(StarRatingValue) > 0 ? (
                        <StarRating
                          rating={parseInt(StarRatingValue) ? parseInt(StarRatingValue) : 0}
                          halfRating={parseInt(StarRatingValue) ? (Number.isInteger(StarRatingValue) ? 0 : 1) : 0}
                          total={5}
                          isInteractive={false}
                          className="mr-8"
                        />
                      ) : null}
                      {review > 0 ? (
                        <div
                          onClick={() => {
                            if (!quickView) {
                              const elmntToView = document.getElementById('Review');
                              elmntToView.scrollIntoView();
                            }
                          }}
                          className="text-14 text-theme font-medium underline cursor-pointer"
                          font-customisation="para-text"
                        >
                          ({reviewsCount} Reviews)
                        </div>
                      ) : (
                        <Link
                          className="text-theme underline"
                          to={
                            getBuyerJWTToken()
                              ? `/product/${product.slug}/review`
                              : `/account/login?redirect_url=/product/${product.slug}/review`
                          }
                        >
                          Write a Review
                        </Link>
                      )}
                    </div>
                    <div className="hidden md:flex">
                      {!quickView ? (
                        <>
                          <div>
                            <ShareModal product={product} />
                          </div>
                          <WishlistIcon
                            className="h-24 w-24 ml-12"
                            button-customisation="wishlist-button"
                            type={wishlist?.isProductInWishList ? 'addWishList' : 'removeWishList'}
                            wishListFeature={wishListFeature}
                            onClick={() => {
                              onWishListClickHandler();
                            }}
                          />
                        </>
                      ) : null}
                    </div>
                  </div>
                  <h1
                    className={classnames('text-28 md:text-36 font-medium text-heading md:leading-50', {
                      'mb-10': quickView,
                      'mb-32': !quickView,
                    })}
                    font-customisation="section-heading"
                    color-customisation="page-heading"
                    id="product-title"
                  >
                    {finalProduct.title}
                  </h1>
                  <ProductAddToCart
                    product={product}
                    addToast={addToast}
                    cartProductQuantity={cartProductQuantity}
                    addProductToCart={addProductToCart}
                    hocArgs={hocArgs}
                    storeType={storeType}
                    quickView={quickView}
                    wishListFeature={wishListFeature}
                  />
                </div>
              </div>
            </div>
            {finalProduct.description && !quickView ? (
              <div className={'product-description bg-white md:mt-48 rounded-md px-16 md:px-0'}>
                <div
                  className="text-heading text-20 md:text-28 mb-20 font-semibold font-sora"
                  font-customisation="section-heading"
                  color-customisation="page-heading"
                >
                  Product Description
                </div>
                <Editor
                  name="productLongDescription"
                  value={finalProduct.description}
                  readonly={true}
                  isDefaultFont={true}
                  className="px-0 py-0"
                />
              </div>
            ) : null}
          </div>
        );
      }}
    />
  );
};

export default ProductComponent;
